import {
  DEFAULT_AXIS_FONT_SIZE,
  DEFAULT_AXIS_LABEL_FONT_SIZE,
  DEFAULT_GRID_RIGHT,
  DEFAULT_LEGEND_FONT_SIZE,
  DEFAULT_TITLE_FONT_SIZE,
  MPC_PRODUCT_LEGEND_HEIGHT,
  MPC_PRODUCT_LEGEND_ID
} from "constants/chart.constants";
import { EvaChart } from "constants/charts.enums";
import { THEME_FONT_FAMILY } from "constants/style.constants";
import type { EChartsType } from "echarts";
import { calculateTextDimensions } from "utils/dom";

import { getLockPosition, getLogButtonPosition } from "./getAxisControlPositions";

type ChartFontSettingsT = {
  titleFontSize: number;
  axisFontSize: number;
  axisLabelFontSize: number;
  legendFontSize: number;
  fontFamily: string;
};

// ECharts' internal _componentsMap key for the first series' grid component
export const GRID_COMPONENT_KEY = "_ec_\u0000series\u00000\u00000_grid";

// Minimum name gap for right y-axes to prevent overlap with log/lock buttons
export const MIN_RIGHT_Y_AXIS_NAME_GAP = 43;

// Extra right padding for screenshots of charts with a single visible y-axis,
// so the last x-axis label isn't clipped at the canvas edge.
const SCREENSHOT_SINGLE_Y_AXIS_RIGHT_PADDING = 40;

// How far the total-rate x-max lock handle sits inside the plot's right edge
// when the count axis is hidden. With the count axis shown, the handle stays
// at the translator's hand-tuned anchor instead (see the lock-position pass).
export const X_MAX_LOCK_INSET_FROM_PLOT_EDGE = 20;

/**
 * True when the chart options contain the MPC product-legend graphic. Searches
 * recursively because ECharts' getOption() normalizes a graphic group into a
 * `{ elements: [...] }` wrapper (screenshot path), whereas the live translator
 * passes it as a top-level `{ id, children }` group. Matching by id in both
 * shapes keeps the legend's bottom-grid reservation consistent across paths.
 */
export const hasProductLegendGraphic = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  graphic: any
): boolean => {
  if (!graphic || typeof graphic !== "object") return false;
  if (Array.isArray(graphic)) return graphic.some(hasProductLegendGraphic);
  if (graphic.id === MPC_PRODUCT_LEGEND_ID) return true;
  if (Array.isArray(graphic.elements))
    return graphic.elements.some(hasProductLegendGraphic);
  if (Array.isArray(graphic.children))
    return graphic.children.some(hasProductLegendGraphic);
  return false;
};

const applySpacingToChartOptions = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  options: any,
  chartInstance: EChartsType,
  fontSettings: ChartFontSettingsT,
  showLogButton: boolean,
  isScreenshot: boolean = false
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): any => {
  if (!options) return options;

  // Determine name gap for each axis
  const yAxisNameGaps = [];
  const yAxisList = Array.isArray(options.yAxis) ? options.yAxis : [options.yAxis ?? {}];
  // Detect MPC mode: both left and right Y axes present
  const hasLeftYAxis = yAxisList.some((a) => !a.position || a.position === "left");
  const hasRightYAxis = yAxisList.some((a) => a.position === "right");
  const isMPC = hasLeftYAxis && hasRightYAxis;
  yAxisList.forEach((axis, i) => {
    const nameGap = getYAxisNameGap(
      chartInstance,
      i,
      fontSettings.axisLabelFontSize,
      fontSettings.fontFamily,
      isMPC,
      axis,
      options.chartType
    );
    axis.nameGap = nameGap;
    yAxisNameGaps.push(nameGap);
  });

  const xAxisNameGaps = [];
  const xAxisList = Array.isArray(options.xAxis) ? options.xAxis : [options.xAxis ?? {}];
  xAxisList.forEach((axis) => {
    const nameGap = getXAxisNameGap(
      fontSettings.axisLabelFontSize,
      fontSettings.fontFamily
    );
    axis.nameGap = nameGap;
    xAxisNameGaps.push(nameGap);
  });

  // Determine offset for axis
  const offsetAmountY = getYAxisOffsets(
    options,
    yAxisNameGaps,
    fontSettings.axisFontSize,
    fontSettings.fontFamily
  );
  yAxisList.forEach((axis, i) => {
    axis.offset = offsetAmountY[i];
  });

  const offsetAmountX = getXAxisOffsets(
    options,
    xAxisNameGaps,
    fontSettings.axisFontSize,
    fontSettings.fontFamily
  );
  xAxisList.forEach((axis, i) => {
    axis.offset = offsetAmountX[i];
  });
  // Determine grid
  const grid = getGridSpacing(
    chartInstance,
    options,
    xAxisNameGaps,
    yAxisNameGaps,
    fontSettings.axisFontSize,
    fontSettings.titleFontSize,
    fontSettings.legendFontSize,
    fontSettings.fontFamily,
    isScreenshot,
    fontSettings.axisLabelFontSize
  );

  // Determine log position
  if (showLogButton) {
    xAxisList.forEach((axis, i) => {
      axis.logPosition = getLogButtonPosition(
        axis.position,
        grid.left,
        grid.right,
        grid.top,
        grid.bottom,
        0,
        offsetAmountX[i]
      );
    });

    yAxisList.forEach((axis, i) => {
      axis.logPosition = getLogButtonPosition(
        axis.position,
        grid.left,
        grid.right,
        grid.top,
        grid.bottom,
        yAxisNameGaps[i],
        offsetAmountY[i]
      );
    });
  }

  // Determine lock positions for multi-axis charts
  xAxisList.forEach((axis, i) => {
    if (axis.isTotalRateDateLockPosition) {
      // Hand-tuned lock positions are kept, except the x-max anchor: it
      // follows the plot's right edge inward when the count axis is hidden and
      // its band collapses. It never moves further left than the translator's
      // count-on position, though — the Well Count toggle is fixed to the left
      // of that spot, and a wide count band (5-digit counts) would otherwise
      // walk the handle onto the switch. Spread (never mutate) — the
      // translator's lockPosition is a shared module constant.
      const countOnRight = axis.lockPosition?.max?.right;
      if (countOnRight != null) {
        axis.lockPosition = {
          ...axis.lockPosition,
          max: {
            ...axis.lockPosition.max,
            right: Math.min(grid.right + X_MAX_LOCK_INSET_FROM_PLOT_EDGE, countOnRight)
          }
        };
      }
      return;
    }
    axis.lockPosition = getLockPosition(
      axis.position,
      grid.left,
      grid.right,
      grid.top,
      grid.bottom,
      0,
      offsetAmountX[i]
    );
  });

  yAxisList.forEach((axis, i) => {
    if (axis.isTotalRateDateLockPosition) return;
    axis.lockPosition = getLockPosition(
      axis.position,
      grid.left,
      grid.right,
      grid.top,
      grid.bottom,
      yAxisNameGaps[i],
      offsetAmountY[i]
    );
  });

  // Determine zoom sliders position
  if (options?.dataZoom?.length > 0) {
    options.dataZoom.forEach((slider) => {
      if (slider.type === "slider") {
        if (slider.xAxisIndex !== undefined) {
          slider.bottom = xAxisNameGaps[slider.xAxisIndex];
        } else if (slider.yAxisIndex !== undefined && slider.right == null) {
          // A right-pinned y slider (rig/frac Activity's y2 mirror) positions
          // itself via `right`; assigning a numeric left here would drag it
          // across the chart onto the left slider. No wells chart pins `right`
          // today and this pass never runs for rig/frac, but guard it so the
          // right-slider pattern can't silently break if either changes.
          slider.left = yAxisNameGaps[slider.yAxisIndex];
        }
      }
    });
  }

  options.grid = grid;

  return options;
};

export default applySpacingToChartOptions;

/**
 * Stacks the axes that share a side so each one clears the previous axis's name
 * gap and title. `farSide` is the position value meaning "the second side" —
 * "top" for x axes, "right" for y axes; every other value (including undefined)
 * counts as the first side.
 *
 * Reading `nameGaps` at the previous same-side index rather than at `i` is
 * deliberate: the space being skipped is the space that axis occupies.
 *
 * `skipHidden` must mirror whether getGridSpacing reserves margin for hidden
 * axes on this side, or the outermost title stacks past the margin and clips.
 * It does skip `show === false` for y axes and does NOT for x axes, so y passes
 * true and x passes false. `show` left undefined means visible, as there.
 */
const getAxisOffsets = (
  axisList: { position?: string; show?: boolean }[],
  nameGaps: number[],
  farSide: "top" | "right",
  skipHidden: boolean,
  axisFontSize: number,
  fontFamily: string
): number[] => {
  const offsetArray: number[] = new Array(axisList.length).fill(0);
  const axisSize = calculateTextDimensions("AXIS", axisFontSize, fontFamily);

  const lastIndexBySide = { far: -1, near: -1 };
  axisList.forEach((axis, i) => {
    const side = axis.position === farSide ? "far" : "near";
    const last = lastIndexBySide[side];
    if (last >= 0) {
      offsetArray[i] = nameGaps[last] + axisSize.height + offsetArray[last];
    }
    // A hidden axis keeps its slot so callers can index by axis position, but
    // it reserves no margin, so it must not push later same-side axes outward.
    if (skipHidden && axis.show === false) return;
    lastIndexBySide[side] = i;
  });

  return offsetArray;
};

export const getYAxisOffsets = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  options: any,
  nameGaps: number[] = [],
  axisFontSize: number = DEFAULT_AXIS_FONT_SIZE,
  fontFamily: string = THEME_FONT_FAMILY
): number[] =>
  getAxisOffsets(
    Array.isArray(options.yAxis) ? options.yAxis : [options.yAxis ?? {}],
    nameGaps,
    "right",
    true,
    axisFontSize,
    fontFamily
  );

export const getXAxisOffsets = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  options: any,
  nameGaps: number[] = [],
  axisFontSize: number = DEFAULT_AXIS_FONT_SIZE,
  fontFamily: string = THEME_FONT_FAMILY
): number[] =>
  getAxisOffsets(
    Array.isArray(options.xAxis) ? options.xAxis : [options.xAxis ?? {}],
    nameGaps,
    "top",
    false,
    axisFontSize,
    fontFamily
  );

// Returns whichever of a and b has the larger magnitude, keeping its sign
// so negative extents are sized too.
const maxByMagnitude = (a: number, b: number): number =>
  Math.abs(a) > Math.abs(b) ? a : b;

// Width of an axis extent rendered as a tick label. Coerces to a number first
// since callers may pass a trimmed string, and String.prototype.toLocaleString
// ignores the formatting options.
const measureAxisExtentLabelWidth = (
  extent: Maybe<number | string>,
  fontSize: number,
  fontFamily: string
): number => {
  const decimalPlaces = extent?.toString().split(".")[1]?.length ?? 0;
  const label =
    extent == null
      ? undefined
      : Number(extent).toLocaleString(undefined, {
          minimumFractionDigits: 0,
          maximumFractionDigits: decimalPlaces
        });
  return calculateTextDimensions(label, fontSize, fontFamily).width;
};

// ECharts' own axisLabel.fontSize default (echarts/lib/coord/axisDefault.js),
// used when an axisLabel sets no fontSize of its own.
const ECHARTS_AXIS_LABEL_FONT_SIZE = 12;
// ECharts' title.subtextStyle.fontSize default (echarts/lib/component/title/install.js).
const ECHARTS_SUBTEXT_FONT_SIZE = 12;

let measureCanvas: Nullable<HTMLCanvasElement> = null;

// Canvas measureText rather than calculateTextDimensions: this runs on every
// options rebuild (including hover), and a DOM span per label forces a reflow.
const measureTextWidth = (
  text: string,
  fontSize: number,
  fontFamily: string,
  fontWeight: string | number = "normal"
): number => {
  try {
    measureCanvas ??= document.createElement("canvas");
    const ctx = measureCanvas.getContext("2d");
    if (ctx) {
      ctx.font = `${fontWeight} ${fontSize}px ${fontFamily}`;
      return Math.ceil(ctx.measureText(text).width);
    }
  } catch {
    // fall through to the DOM measurement
  }
  return calculateTextDimensions(text, fontSize, fontFamily).width;
};

const getCategoryAxisLabelWidth = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  axisOptions: any,
  fontFamily: string
): number => {
  const maxWidth: Maybe<number> = axisOptions.axisLabel?.width;
  const data = Array.isArray(axisOptions.data) ? axisOptions.data : [];
  if (data.length === 0) return maxWidth ?? 0;

  // Measure in the font the labels render in: ECharts reads only axisLabel's
  // own fontSize/fontWeight, not the global textStyle.
  const fontSize = axisOptions.axisLabel?.fontSize ?? ECHARTS_AXIS_LABEL_FONT_SIZE;
  const fontWeight = axisOptions.axisLabel?.fontWeight ?? "normal";

  const widest = data.reduce((max, item) => {
    const label = String(item?.value ?? item ?? "");
    return Math.max(max, measureTextWidth(label, fontSize, fontFamily, fontWeight));
  }, 0);
  return maxWidth != null ? Math.min(widest, maxWidth) : widest;
};

export const getYAxisNameGap = (
  chartInstance,
  i: number,
  axisLabelFontSize: number = DEFAULT_AXIS_LABEL_FONT_SIZE,
  fontFamily: string = THEME_FONT_FAMILY,
  isMPC: boolean = false,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  axisOptions?: any,
  chartType?: EvaChart
): number => {
  try {
    axisLabelFontSize = axisLabelFontSize ?? DEFAULT_AXIS_LABEL_FONT_SIZE;

    // Scoped to StackedBarHorizontal specifically (not "any category y-axis")
    // — rigFracRecentActivity.ts and wellStatusCurrent.ts also put a category
    // axis on y, and fall into the numeric-extent branch below on purpose;
    // widening this to every category y-axis would silently retune their
    // spacing too, with no test coverage for that behaviour change.
    //
    // For StackedBarHorizontal: the axis has no numeric extent to format —
    // the logic below treats the axis as a value axis and would size the gap
    // to fit a formatted TICK INDEX, not the actual category text. The
    // translator's axisLabel.width is only a truncation cap, so size the gap
    // to the widest rendered label up to that cap.
    if (axisOptions?.type === "category" && chartType === EvaChart.StackedBarHorizontal) {
      const padding = 20;
      return getCategoryAxisLabelWidth(axisOptions, fontFamily) + padding;
    }

    let extent = null;
    if (chartInstance && chartInstance._componentsMap[GRID_COMPONENT_KEY]) {
      const axis =
        chartInstance._componentsMap[GRID_COMPONENT_KEY].__model.coordinateSystem._axesMap
          ?.y[i];

      let padding = 20;
      if (axis && axis.scale && axis.scale.type) {
        // both min and max are compared to find the max absolute value
        // as negative values are also considered
        if (axis.scale.type === "log") {
          const maxAbsoluteExtent = maxByMagnitude(
            axis.scale?.rawExtentInfo._dataMax,
            axis.scale?.rawExtentInfo._dataMin
          );
          const exponent = Math.ceil(Math.log10(maxAbsoluteExtent));
          extent = Math.pow(10, exponent);
        } else {
          extent = maxByMagnitude(axis.scale?._extent[0], axis.scale?._extent[1]);
          if (axis.scale?._interval < 1) {
            padding = 30;
          }
        }
      }

      if (axis?.position === "right") {
        padding = 30;
      }

      if (extent) {
        const num = Number(extent);
        // Trim strings that are between 0.1 and 1 to ensure we don't use too many decimals.
        if (num && num < 1 && num > 0.1) {
          extent = extent?.toString().substring(0, 3);
        }
        // Trim strings that are between 0.1 and 1 to ensure we don't use too many decimals.
        if (num && num < 100 && num > 1) {
          extent = extent?.toString().substring(0, 3);
        }
      }

      // Preserve decimal place to accurately calculate nameGap
      const computedNameGap =
        measureAxisExtentLabelWidth(extent, axisLabelFontSize, fontFamily) + padding;

      // Enforce minimum gap for right-side Y axes in MPC charts to maintain consistent spacing
      if (axis?.position === "right" && isMPC) {
        return Math.max(computedNameGap, MIN_RIGHT_Y_AXIS_NAME_GAP);
      }

      return computedNameGap;
    }
  } catch {
    //suppress error
    //TODO wait for front end logging
  }
  return 30;
};

export const getXAxisNameGap = (
  axisLabelFontSize: number = DEFAULT_AXIS_LABEL_FONT_SIZE,
  fontFamily: string = THEME_FONT_FAMILY
): number => {
  const padding = 8;
  return calculateTextDimensions("1", axisLabelFontSize, fontFamily).height + padding;
};

// Scoped to StackedBarHorizontal specifically — its numeric axis is x, the
// opposite of every other chart type (where a numeric axis sizes its OWN
// margin via getYAxisNameGap above; grid.right here is otherwise only ever
// widened for a right-side y-axis, never for x). DEFAULT_GRID_RIGHT (20px)
// is sized for a typical compact x-axis label (dates, small categories) —
// nowhere near enough for this chart's tick labels, which can run to 6-7
// digits once a bucket sums many wells' Oil Cum To Date. The narrower plot
// that this chart's (correctly) wide category-axis nameGap produces made the
// rightmost tick's label clip at the canvas edge routinely. Mirrors
// getYAxisNameGap's extent-based sizing above, against the x axis instead.
export const getStackedBarHorizontalGridRight = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  chartInstance: any,
  axisLabelFontSize: number = DEFAULT_AXIS_LABEL_FONT_SIZE,
  fontFamily: string = THEME_FONT_FAMILY
): number => {
  try {
    const grid = chartInstance?._componentsMap?.[GRID_COMPONENT_KEY];
    const axis = grid?.__model.coordinateSystem._axesMap?.x?.[0];
    if (!axis?.scale) return DEFAULT_GRID_RIGHT;

    let extent: number | string =
      axis.scale.type === "log"
        ? maxByMagnitude(
            axis.scale?.rawExtentInfo._dataMax,
            axis.scale?.rawExtentInfo._dataMin
          )
        : maxByMagnitude(axis.scale?._extent[0], axis.scale?._extent[1]);
    if (!extent) return DEFAULT_GRID_RIGHT;

    const num = Number(extent);
    if (num && num > -1 && num < 1) {
      extent = extent.toString().substring(0, 4);
    }
    const labelWidth = measureAxisExtentLabelWidth(extent, axisLabelFontSize, fontFamily);

    // ECharts centers a tick's label on its position, so only roughly half
    // the rightmost label's width actually extends past the plot's right
    // edge into this reserved margin.
    return Math.max(DEFAULT_GRID_RIGHT, Math.ceil(labelWidth / 2) + 16);
  } catch {
    return DEFAULT_GRID_RIGHT;
  }
};

export const getLegendHeight = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  options: any,
  legendFontSize: number,
  fontFamily: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  chartInstance: any
): number => {
  if (!options.legend?.show) return 0;

  // Check rendered component views — populated synchronously by setOption.
  // Require height > 5 to exclude sub-pixel floating-point artifacts (e.g. 0.4px)
  // that can appear when the legend group is in an intermediate state.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const componentsViews: any[] = chartInstance._componentsViews ?? [];
  for (const view of componentsViews) {
    if (view?.type?.includes?.("legend")) {
      const rect = view.group?.getBoundingRect?.();
      if (rect?.height > 5) return rect.height;
    }
  }

  // Fallback: estimate rows from measured label widths.
  const measureWidth = (text: string): number =>
    measureTextWidth(text, legendFontSize, fontFamily);

  // A `legend.data` entry is either the name itself or a `{ name, icon }`
  // object, so the object form has to be unwrapped - stringifying it whole
  // measures "[object Object]".
  const legendEntryName = (entry: unknown): string =>
    entry !== null && typeof entry === "object"
      ? String((entry as { name?: unknown }).name ?? "")
      : String(entry ?? "");

  /*
   * What the legend will actually list, which is not the same as the series.
   * The MPC option carries `legend.data` filtered to the historical series -
   * every product also has a Forecast series, and those are left out of the
   * legend. Estimating from `series` counted them, so a chart in a narrow box
   * was credited with two or three rows it never draws, and the grid gave up
   * that much of its height to a legend that did not need it.
   */
  const seriesArray: { name?: string }[] = Array.isArray(options.legend?.data)
    ? options.legend.data.map((entry: unknown) => ({ name: legendEntryName(entry) }))
    : Array.isArray(options.series)
    ? options.series
    : [];
  const rowHeight = calculateTextDimensions("Wj", legendFontSize, fontFamily).height;
  if (seriesArray.length === 0) return rowHeight;

  const containerWidth: number = chartInstance.getWidth?.() ?? 600;
  const legendPadding = options.legend?.padding ?? 5;
  const paddingH: number = Array.isArray(legendPadding)
    ? (legendPadding[1] ?? 5) + (legendPadding[3] ?? legendPadding[1] ?? 5)
    : legendPadding * 2;
  const effectiveWidth = containerWidth - paddingH;
  const itemIconWidth: number = options.legend?.itemWidth ?? 25;
  const itemGap: number = options.legend?.itemGap ?? 10;

  let rowWidth = 0;
  let rows = 1;
  for (const series of seriesArray) {
    const textWidth = measureWidth(series.name ?? "");
    const itemWidth = itemIconWidth + 5 + textWidth + itemGap;
    if (rowWidth > 0 && rowWidth + itemWidth > effectiveWidth) {
      rows++;
      rowWidth = itemWidth;
    } else {
      rowWidth += itemWidth;
    }
  }
  return rowHeight * rows;
};

export const getGridSpacing = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  chartInstance: any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  options: any,
  xAxisNameGaps: number[] = [],
  yAxisNameGaps: number[] = [],
  axisFontSize: number = DEFAULT_AXIS_FONT_SIZE,
  titleFontSize: number = DEFAULT_TITLE_FONT_SIZE,
  legendFontSize: number = DEFAULT_LEGEND_FONT_SIZE,
  fontFamily: string = THEME_FONT_FAMILY,
  isScreenShot: boolean = false,
  axisLabelFontSize: number = DEFAULT_AXIS_LABEL_FONT_SIZE
): { top: number; bottom: number; left: number; right: number } => {
  const grid = {
    top: 0,
    bottom: 0,
    left: 0,
    right: 0
  };
  const titleDimensions = calculateTextDimensions("TITLE", titleFontSize, fontFamily);
  const axisTitleDimensions = calculateTextDimensions("AXIS", axisFontSize, fontFamily);

  const xAxisList = Array.isArray(options.xAxis) ? options.xAxis : [options.xAxis ?? {}];
  xAxisList.forEach((axis, i) => {
    if (axis.position === "top") {
      grid.top += xAxisNameGaps[i] + axisTitleDimensions.height;
    } else {
      grid.bottom += xAxisNameGaps[i] + axisTitleDimensions.height;
    }
  });

  const yAxisList = Array.isArray(options.yAxis) ? options.yAxis : [options.yAxis ?? {}];
  // A hidden axis (show === false, e.g. the total-rate chart's well count axis
  // when the count toggle is off) renders nothing, so it must not reserve
  // margin — otherwise the plot keeps a blank band where the axis used to be.
  // `show` left undefined means visible (the ECharts default): translators
  // that toggle an axis emit an explicit boolean; the permissive rule here
  // exists for the many charts that omit `show` entirely.
  const visibleYAxes = yAxisList.filter((axis) => axis.show !== false);
  yAxisList.forEach((axis, i) => {
    if (axis.show === false) return;
    if (axis.position === "right") {
      grid.right += yAxisNameGaps[i] + axisTitleDimensions.height;
    } else {
      grid.left += yAxisNameGaps[i] + axisTitleDimensions.height;
    }
  });

  // Get the space used for the legend.
  const legendOffset = options.legend?.show
    ? getLegendHeight(options, legendFontSize, fontFamily, chartInstance)
    : 0;

  // The MPC product legend is drawn as a `graphic` group (not a native legend),
  // so reserve a band for it below the axes when present. Detection must be
  // robust to ECharts' getOption() normalization: the live translator emits
  // `[{ id, type:'group', children }]`, but getOption() (the screenshot path)
  // flattens it to `[{ elements: [{ id, ... }] }]`, so a top-level id check
  // alone misses it in screenshot mode (which left it overlapping the axes).
  const productLegendOffset = hasProductLegendGraphic(options.graphic)
    ? MPC_PRODUCT_LEGEND_HEIGHT
    : 0;

  const titlePadding = options.title?.padding
    ? options.title?.padding[0] + options.title?.padding[2]
    : 0;
  // A subtitle sits itemGap below the title, so its line needs room too or it overlaps the plot.
  const subtitleHeight = options.title?.subtext
    ? (options.title.itemGap ?? 0) +
      calculateTextDimensions(
        options.title.subtext,
        options.title.subtextStyle?.fontSize ?? ECHARTS_SUBTEXT_FONT_SIZE,
        fontFamily
      ).height
    : 0;
  grid.top =
    (grid.top > 0 ? grid.top : 0) +
    titleDimensions.height +
    titlePadding +
    subtitleHeight;
  grid.bottom = (grid.bottom > 0 ? grid.bottom : 20) + legendOffset + productLegendOffset;
  grid.left = grid.left > 0 ? grid.left : 20;
  grid.right = grid.right > 0 ? grid.right : DEFAULT_GRID_RIGHT;
  if (options.chartType === EvaChart.StackedBarHorizontal) {
    grid.right = Math.max(
      grid.right,
      getStackedBarHorizontalGridRight(chartInstance, axisLabelFontSize, fontFamily)
    );
  }
  // Counts VISIBLE axes: a total-rate chart with the well count hidden renders
  // one y-axis, so its screenshot is padded like any other single-axis chart
  // and the plot edge lines up across a dashboard's screenshots.
  grid.right =
    isScreenShot && visibleYAxes.length === 1
      ? grid.right + SCREENSHOT_SINGLE_Y_AXIS_RIGHT_PADDING
      : grid.right;

  return grid;
};
